import { SolanaSignAndSendTransaction, SolanaSignIn, SolanaSignMessage, SolanaSignTransaction } from '@solana/wallet-standard-features';
import { WalletStandardError, WALLET_STANDARD_ERROR__FEATURES__WALLET_ACCOUNT_CHAIN_UNSUPPORTED } from '@wallet-standard/errors';
import { getWalletAccountFeature, getWalletFeature } from '@wallet-standard/ui';
import { getWalletAccountForUiWalletAccount_DO_NOT_USE_OR_YOU_WILL_BE_FIRED, getWalletForHandle_DO_NOT_USE_OR_YOU_WILL_BE_FIRED, getOrCreateUiWalletAccountForStandardWalletAccount_DO_NOT_USE_OR_YOU_WILL_BE_FIRED } from '@wallet-standard/ui-registry';
import { useCallback, useMemo, useRef } from 'react';
import { address } from '@solana/addresses';
import { SolanaError, SOLANA_ERROR__SIGNER__WALLET_MULTISIGN_UNIMPLEMENTED } from '@solana/errors';
import { getAbortablePromise } from '@solana/promises';
import { getTransactionCodec, getTransactionEncoder } from '@solana/transactions';

// src/useSignAndSendTransaction.ts
function useSignAndSendTransaction(uiWalletAccount, chain) {
  const signAndSendTransactions = useSignAndSendTransactions(uiWalletAccount, chain);
  return useCallback(
    async (input) => {
      const [result] = await signAndSendTransactions(input);
      return result;
    },
    [signAndSendTransactions]
  );
}
function useSignAndSendTransactions(uiWalletAccount, chain) {
  if (!uiWalletAccount.chains.includes(chain)) {
    throw new WalletStandardError(WALLET_STANDARD_ERROR__FEATURES__WALLET_ACCOUNT_CHAIN_UNSUPPORTED, {
      address: uiWalletAccount.address,
      chain,
      featureName: SolanaSignAndSendTransaction,
      supportedChains: [...uiWalletAccount.chains],
      supportedFeatures: [...uiWalletAccount.features]
    });
  }
  const signAndSendTransactionFeature = getWalletAccountFeature(
    uiWalletAccount,
    SolanaSignAndSendTransaction
  );
  const account = getWalletAccountForUiWalletAccount_DO_NOT_USE_OR_YOU_WILL_BE_FIRED(uiWalletAccount);
  return useCallback(
    async (...inputs) => {
      const inputsWithChainAndAccount = inputs.map(({ options, ...rest }) => {
        const minContextSlot = options?.minContextSlot;
        return {
          ...rest,
          account,
          chain,
          ...minContextSlot != null ? {
            options: {
              minContextSlot: Number(minContextSlot)
            }
          } : null
        };
      });
      const results = await signAndSendTransactionFeature.signAndSendTransaction(...inputsWithChainAndAccount);
      return results;
    },
    [account, chain, signAndSendTransactionFeature]
  );
}
function useSignIn(uiWalletHandle) {
  const signIns = useSignIns(uiWalletHandle);
  return useCallback(
    async (input) => {
      const [result] = await signIns(input);
      return result;
    },
    [signIns]
  );
}
function useSignIns(uiWalletHandle) {
  let signMessageFeature;
  if ("address" in uiWalletHandle && typeof uiWalletHandle.address === "string") {
    getWalletAccountForUiWalletAccount_DO_NOT_USE_OR_YOU_WILL_BE_FIRED(uiWalletHandle);
    signMessageFeature = getWalletAccountFeature(
      uiWalletHandle,
      SolanaSignIn
    );
  } else {
    signMessageFeature = getWalletFeature(uiWalletHandle, SolanaSignIn);
  }
  const wallet = getWalletForHandle_DO_NOT_USE_OR_YOU_WILL_BE_FIRED(uiWalletHandle);
  return useCallback(
    async (...inputs) => {
      const inputsWithAddressAndChainId = inputs.map((input) => ({
        ...input,
        // Prioritize the `UiWalletAccount` address if it exists.
        ..."address" in uiWalletHandle ? { address: uiWalletHandle.address } : null
      }));
      const results = await signMessageFeature.signIn(...inputsWithAddressAndChainId);
      const resultsWithoutSignatureType = results.map(
        ({
          account,
          signatureType: _,
          // Solana signatures are always of type `ed25519` so drop this property.
          ...rest
        }) => ({
          ...rest,
          account: getOrCreateUiWalletAccountForStandardWalletAccount_DO_NOT_USE_OR_YOU_WILL_BE_FIRED(
            wallet,
            account
          )
        })
      );
      return resultsWithoutSignatureType;
    },
    [signMessageFeature, uiWalletHandle, wallet]
  );
}
function useSignMessage(...config) {
  const signMessages = useSignMessages(...config);
  return useCallback(
    async (input) => {
      const [result] = await signMessages(input);
      return result;
    },
    [signMessages]
  );
}
function useSignMessages(uiWalletAccount) {
  const signMessageFeature = getWalletAccountFeature(
    uiWalletAccount,
    SolanaSignMessage
  );
  const account = getWalletAccountForUiWalletAccount_DO_NOT_USE_OR_YOU_WILL_BE_FIRED(uiWalletAccount);
  return useCallback(
    async (...inputs) => {
      const inputsWithAccount = inputs.map((input) => ({ ...input, account }));
      const results = await signMessageFeature.signMessage(...inputsWithAccount);
      const resultsWithoutSignatureType = results.map(
        ({
          signatureType: _,
          // Solana signatures are always of type `ed25519` so drop this property.
          ...rest
        }) => rest
      );
      return resultsWithoutSignatureType;
    },
    [signMessageFeature, account]
  );
}
function useSignTransaction(uiWalletAccount, chain) {
  const signTransactions = useSignTransactions(uiWalletAccount, chain);
  return useCallback(
    async (input) => {
      const [result] = await signTransactions(input);
      return result;
    },
    [signTransactions]
  );
}
function useSignTransactions(uiWalletAccount, chain) {
  if (!uiWalletAccount.chains.includes(chain)) {
    throw new WalletStandardError(WALLET_STANDARD_ERROR__FEATURES__WALLET_ACCOUNT_CHAIN_UNSUPPORTED, {
      address: uiWalletAccount.address,
      chain,
      featureName: SolanaSignAndSendTransaction,
      supportedChains: [...uiWalletAccount.chains],
      supportedFeatures: [...uiWalletAccount.features]
    });
  }
  const signTransactionFeature = getWalletAccountFeature(
    uiWalletAccount,
    SolanaSignTransaction
  );
  const account = getWalletAccountForUiWalletAccount_DO_NOT_USE_OR_YOU_WILL_BE_FIRED(uiWalletAccount);
  return useCallback(
    async (...inputs) => {
      const inputsWithAccountAndChain = inputs.map(({ options, ...rest }) => {
        const minContextSlot = options?.minContextSlot;
        return {
          ...rest,
          account,
          chain,
          ...minContextSlot != null ? {
            options: {
              minContextSlot: Number(minContextSlot)
            }
          } : null
        };
      });
      const results = await signTransactionFeature.signTransaction(...inputsWithAccountAndChain);
      return results;
    },
    [signTransactionFeature, account, chain]
  );
}
function useWalletAccountMessageSigner(uiWalletAccount) {
  const signMessage = useSignMessage(uiWalletAccount);
  return useMemo(
    () => ({
      address: address(uiWalletAccount.address),
      async modifyAndSignMessages(messages, config) {
        config?.abortSignal?.throwIfAborted();
        if (messages.length > 1) {
          throw new SolanaError(SOLANA_ERROR__SIGNER__WALLET_MULTISIGN_UNIMPLEMENTED);
        }
        if (messages.length === 0) {
          return messages;
        }
        const { content: originalMessage, signatures: originalSignatureMap } = messages[0];
        const input = {
          message: originalMessage
        };
        const { signedMessage, signature } = await getAbortablePromise(signMessage(input), config?.abortSignal);
        const messageWasModified = originalMessage.length !== signedMessage.length || originalMessage.some((originalByte, ii) => originalByte !== signedMessage[ii]);
        const originalSignature = originalSignatureMap[uiWalletAccount.address];
        const signatureIsNew = !originalSignature?.every((originalByte, ii) => originalByte === signature[ii]);
        if (!signatureIsNew && !messageWasModified) {
          return messages;
        }
        const nextSignatureMap = messageWasModified ? { [uiWalletAccount.address]: signature } : { ...originalSignatureMap, [uiWalletAccount.address]: signature };
        const outputMessages = Object.freeze([
          Object.freeze({
            content: signedMessage,
            signatures: Object.freeze(nextSignatureMap)
          })
        ]);
        return outputMessages;
      }
    }),
    [uiWalletAccount, signMessage]
  );
}
function useWalletAccountTransactionSigner(uiWalletAccount, chain) {
  const encoderRef = useRef(null);
  const signTransaction = useSignTransaction(uiWalletAccount, chain);
  return useMemo(
    () => ({
      address: address(uiWalletAccount.address),
      async modifyAndSignTransactions(transactions, config = {}) {
        const { abortSignal, ...options } = config;
        abortSignal?.throwIfAborted();
        const transactionCodec = encoderRef.current ||= getTransactionCodec();
        if (transactions.length > 1) {
          throw new SolanaError(SOLANA_ERROR__SIGNER__WALLET_MULTISIGN_UNIMPLEMENTED);
        }
        if (transactions.length === 0) {
          return transactions;
        }
        const [transaction] = transactions;
        const wireTransactionBytes = transactionCodec.encode(transaction);
        const inputWithOptions = {
          ...options,
          transaction: wireTransactionBytes
        };
        const { signedTransaction } = await getAbortablePromise(signTransaction(inputWithOptions), abortSignal);
        const decodedSignedTransaction = transactionCodec.decode(
          signedTransaction
        );
        return Object.freeze([decodedSignedTransaction]);
      }
    }),
    [uiWalletAccount.address, signTransaction]
  );
}
function useWalletAccountTransactionSendingSigner(uiWalletAccount, chain) {
  const encoderRef = useRef(null);
  const signAndSendTransaction = useSignAndSendTransaction(uiWalletAccount, chain);
  return useMemo(
    () => ({
      address: address(uiWalletAccount.address),
      async signAndSendTransactions(transactions, config = {}) {
        const { abortSignal, ...options } = config;
        abortSignal?.throwIfAborted();
        const transactionEncoder = encoderRef.current ||= getTransactionEncoder();
        if (transactions.length > 1) {
          throw new SolanaError(SOLANA_ERROR__SIGNER__WALLET_MULTISIGN_UNIMPLEMENTED);
        }
        if (transactions.length === 0) {
          return [];
        }
        const [transaction] = transactions;
        const wireTransactionBytes = transactionEncoder.encode(transaction);
        const inputWithOptions = {
          ...options,
          transaction: wireTransactionBytes
        };
        const { signature } = await getAbortablePromise(signAndSendTransaction(inputWithOptions), abortSignal);
        return Object.freeze([signature]);
      }
    }),
    [signAndSendTransaction, uiWalletAccount.address]
  );
}

export { useSignAndSendTransaction, useSignIn, useSignMessage, useSignTransaction, useWalletAccountMessageSigner, useWalletAccountTransactionSendingSigner, useWalletAccountTransactionSigner };
//# sourceMappingURL=index.browser.mjs.map
//# sourceMappingURL=index.browser.mjs.map